import React, { useEffect, useState } from 'react';
import { LocalizationProvider } from './engine/localization/context';
import { applyThemeTokens } from './engine/branding/theme';
import { getClientConfig, resolveClientIdFromHostname } from './config';
import { updateClientDomMetadata } from './engine/metadata/metadataEngine';
import { Header } from './components/Header';
import { QuickActions } from './components/QuickActions';
import { SocialBar } from './components/SocialBar';
import { ServicesList } from './components/ServicesList';
import { SaveAndShare } from './components/SaveAndShare';
import { VirtualTourCta } from './components/VirtualTourCta';
import { QrModal } from './components/QrModal';
import { ContactModal } from './components/ContactModal';
import { AiAssistantTrigger } from './components/AiAssistantTrigger';
import { AboutSection } from './components/AboutSection';
import { Footer } from './components/Footer';
import { DemoContactProvider } from './engine/adapters/contact/demoProvider';
import { LandingPage } from './pages/LandingPage';

export const App: React.FC = () => {
  // Check if viewing the Smart Digital Profile Landing Page
  const [isLandingPage, setIsLandingPage] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      return path.includes('smart-profile') || params.get('page') === 'landing';
    }
    return false;
  });

  const [activeClientId, setActiveClientId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return resolveClientIdFromHostname(window.location.hostname, params);
    }
    return 'jetown';
  });
  const [isQrOpen, setIsQrOpen] = useState<boolean>(false);
  const [isContactOpen, setIsContactOpen] = useState<boolean>(false);

  const config = getClientConfig(activeClientId);
  const contactProvider = new DemoContactProvider();

  // Apply theme tokens and update DOM title/favicon/meta whenever active client changes
  useEffect(() => {
    if (!isLandingPage) {
      applyThemeTokens(config.branding);
      updateClientDomMetadata(config, config.defaultLocale);
    } else {
      document.title = 'Smart Digital Profile | by JetOwn Platform';
    }
  }, [config, isLandingPage]);

  // Listen for browser popstate
  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      setIsLandingPage(path.includes('smart-profile') || params.get('page') === 'landing');
      setActiveClientId(resolveClientIdFromHostname(window.location.hostname, params));
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const handleToggleClient = () => {
    setActiveClientId((prev) => {
      if (prev === 'jetown') return 'mgr';
      if (prev === 'mgr') return 'demo';
      return 'jetown';
    });
  };

  // If viewing landing page — wrap in its own provider so landing components
  // (PricingCard etc.) can safely call useLocalization()
  if (isLandingPage) {
    return (
      <LocalizationProvider defaultLocale="ar" supportedLocales={['ar', 'en']}>
        <LandingPage
          onBackToProfile={() => {
            if (window.history.pushState) {
              window.history.pushState({}, '', '/');
            }
            setIsLandingPage(false);
          }}
        />
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider
      defaultLocale={config.defaultLocale}
      supportedLocales={config.supportedLocales}
      key={activeClientId} // Reset locale on client toggle
    >
      <main className="min-h-screen w-full flex items-center justify-center p-2.5 xs:p-4 sm:p-6 md:p-10 transition-colors duration-500 selection:bg-sky-500 selection:text-white overflow-x-hidden">
        {/* Ambient radial glows */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-sky-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl" />
        </div>

        {/* Central Profile Card Container - Mobile-first full available width with safe intentional padding */}
        <div className="relative z-10 w-full max-w-[540px] rounded-[24px] sm:rounded-[32px] bg-slate-900/80 backdrop-blur-2xl border border-sky-500/20 shadow-[0_25px_70px_rgba(0,0,0,0.6),0_0_50px_rgba(56,189,248,0.1)] p-3.5 xs:p-4 sm:p-7 overflow-hidden transition-all duration-300">
          {/* Header & Identity */}
          <Header
            config={config}
            activeClientId={activeClientId}
            onToggleClient={handleToggleClient}
          />

          {/* Social Links Bar & VR / Metaverse Ecosystem Link */}
          {config.features.socialGrid && (
            <SocialBar
              socials={config.socials}
              virtualTour={config.virtualTour}
            />
          )}

          {/* Quick Smart Actions (WhatsApp, Phone, Email, Website) */}
          <QuickActions contacts={config.contacts} />

          {/* Primary Save Contact & Share Action Hub */}
          <SaveAndShare
            config={config}
            onOpenQr={() => setIsQrOpen(true)}
            onOpenContact={() => setIsContactOpen(true)}
          />

          {/* Virtual Tour / VR Experience CTA */}
          <VirtualTourCta virtualTour={config.virtualTour} />

          {/* About / Who We Are Section */}
          {config.about && (
            <AboutSection about={config.about} />
          )}

          {/* Products / Solutions Showcase */}
          {config.features.servicesShowcase && (
            <ServicesList services={config.services} title={config.servicesTitle} />
          )}

          {/* Profile Footer */}
          <Footer config={config} />
        </div>

        {/* Modals & Triggers */}
        {config.features.qrModal && (
          <QrModal
            isOpen={isQrOpen}
            onClose={() => setIsQrOpen(false)}
            profileUrl={config.profileUrl}
            clientName={config.identity.name.en || config.slug}
            branding={config.branding}
            logoUrl={config.identity.logoUrl}
            qrConfig={config.qr}
          />
        )}

        {config.features.contactForm && (
          <ContactModal
            isOpen={isContactOpen}
            onClose={() => setIsContactOpen(false)}
            clientId={config.id}
            provider={contactProvider}
          />
        )}

        {config.features.aiAssistant && (
          <AiAssistantTrigger config={config.aiAssistant} />
        )}
      </main>
    </LocalizationProvider>
  );
};
